import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { errMsg, fmtDate, fmtMoney, fmtQty, downloadReport } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CostCodeHeader, AccountPicker } from "../components/CostCode";
import { WOEditDialog, WOCancelDialog, WOReopenDialog, WO_OPEN_STATUSES } from "../components/WODialogs";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Play, Square, CheckSquare, Ban, PackageMinus, ShoppingCart, AlertTriangle, ExternalLink, Printer, FileText, History, Timer, Pencil, RotateCcw } from "lucide-react";

const EQ_STATUSES = ["operational","out_of_service","reclamation","standby","scheduled_maintenance"];
const EQ_STATUS_LABEL = {operational:"Operativo",out_of_service:"Fuera de servicio",reclamation:"Aprovechamiento",standby:"Stand-by",scheduled_maintenance:"Mant. programado"};
const SHIFTS = ["morning","afternoon","night"];
const SHIFT_LABEL = {morning:"Matutino",afternoon:"Vespertino",night:"Nocturno"};
const SYSTEMS = ["electrical","engine","transmission","hydraulic","pneumatic","brakes","structural","cooling","fuel","steering","cabin","other"];
const SYS_LABEL = {electrical:"Eléctrico",engine:"Motor",transmission:"Transmisión",hydraulic:"Hidráulico",pneumatic:"Neumático",brakes:"Frenos",structural:"Estructural",cooling:"Refrigeración",fuel:"Combustible",steering:"Dirección",cabin:"Cabina",other:"Otro"};

/**
 * Live H.H counter for a WO that is "in_progress".
 * Renders "H.H · 03:42:18 · 7.4 h (× 2 tec)" and refreshes every second.
 */
function LiveHHTimer({ actualStart, techs = 1 }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!actualStart) return null;
  const startMs = new Date(actualStart).getTime();
  if (!startMs || isNaN(startMs)) return null;
  const elapsedMs = Math.max(0, now - startMs);
  const totalSec = Math.floor(elapsedMs / 1000);
  const h = String(Math.floor(totalSec / 3600)).padStart(2, "0");
  const m = String(Math.floor((totalSec % 3600) / 60)).padStart(2, "0");
  const s = String(totalSec % 60).padStart(2, "0");
  const hh = (elapsedMs / 3600000 * Math.max(techs, 1)).toFixed(2);
  return (
    <div className="inline-flex items-center gap-2 border-2 border-primary bg-primary/5 px-3 py-1.5 rounded-none"
         data-testid="wo-live-timer" title="Timer en vivo — H.H se sumará al fin real">
      <Timer size={14} className="text-primary animate-pulse" />
      <div className="flex flex-col leading-tight">
        <div className="text-[8px] font-bold uppercase tracking-widest text-primary">H.H en vivo · × {techs}</div>
        <div className="text-sm font-black tabular-nums text-primary">
          {h}:{m}:{s} <span className="text-[11px] font-bold text-muted-foreground">· {hh} h</span>
        </div>
      </div>
    </div>
  );
}

/** Audit trail rendering for the WO detail page. */
function WOAuditHistory({ wid }) {
  const [entries, setEntries] = useState([]);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    api.get(`/mnt/work-orders/${wid}/audit`).then((r) => setEntries(r.data)).catch(() => setEntries([]));
  }, [wid, expanded]);
  const fmtVal = (v) => {
    if (v == null || v === "") return "—";
    if (Array.isArray(v)) return v.join(", ") || "—";
    if (typeof v === "number") return String(v);
    if (typeof v === "string" && v.length > 10 && v.includes("T")) return fmtDate(v);
    return String(v);
  };
  return (
    <div className="mt-4 pt-4 border-t" data-testid="wo-audit-section">
      <button className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-primary hover:underline"
              onClick={() => setExpanded((x) => !x)}
              data-testid="wo-audit-toggle">
        <History size={12} />
        Historial de cambios ({entries.length})
        <span className="text-muted-foreground">{expanded ? "▲" : "▼"}</span>
      </button>
      {expanded && (
        entries.length === 0 ? (
          <p className="text-[11px] text-muted-foreground italic py-2">Sin cambios registrados aún</p>
        ) : (
          <div className="tbl-wrap max-h-96 overflow-y-auto mt-2" data-testid="wo-audit-list">
            <table className="tbl">
              <thead><tr>
                <th>Fecha</th><th>Usuario</th><th>Campo</th><th>Antes</th><th>Después</th>
              </tr></thead>
              <tbody>
                {entries.map((e, i) => (
                  <tr key={i} data-testid={`wo-audit-row-${i}`}>
                    <td className="text-[10px] tabular-nums text-muted-foreground">{fmtDate(e.at)}</td>
                    <td className="text-[11px]">{e.user}</td>
                    <td className="text-[11px] font-bold">
                      {e.kind === "status" ? "Estado" : (e.field_label || e.field)}
                    </td>
                    <td className="text-[11px] text-muted-foreground line-through">
                      {e.kind === "status" ? e.from : fmtVal(e.old_value)}
                    </td>
                    <td className="text-[11px] font-bold text-primary">
                      {e.kind === "status" ? e.to : fmtVal(e.new_value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

function OperationalEditor({ wo, onSave }) {
  const [form, setForm] = useState({
    equipment_status: wo.equipment_status || "",
    intervention_system: wo.intervention_system || "",
    hourmeter_start: wo.hourmeter_start || 0,
    hourmeter_end: wo.hourmeter_end || 0,
    shift: wo.shift || "",
    technicians_count: wo.technicians_count || 1,
    supervisor: wo.supervisor || "",
    planned_tools: (wo.planned_tools || []).join(", "),
    unplanned_tools: (wo.unplanned_tools || []).join(", "),
  });
  const commit = () => onSave({
    ...form,
    hourmeter_start: parseFloat(form.hourmeter_start) || 0,
    hourmeter_end: parseFloat(form.hourmeter_end) || 0,
    technicians_count: parseInt(form.technicians_count) || 1,
    planned_tools: form.planned_tools.split(",").map(s => s.trim()).filter(Boolean),
    unplanned_tools: form.unplanned_tools.split(",").map(s => s.trim()).filter(Boolean),
  });
  const Field = ({ label, children }) => (
    <div className="space-y-1"><Label className="text-[9px] font-bold uppercase tracking-widest">{label}</Label>{children}</div>
  );
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3 p-3 bg-muted/30 border">
      <Field label="Estado del equipo">
        <select value={form.equipment_status} onChange={(e) => setForm({...form, equipment_status: e.target.value})}
          className="w-full border h-8 text-xs bg-background" data-testid="wo-op-status">
          <option value="">—</option>{EQ_STATUSES.map(s => <option key={s} value={s}>{EQ_STATUS_LABEL[s]}</option>)}
        </select>
      </Field>
      <Field label="Sistema intervenido">
        <select value={form.intervention_system} onChange={(e) => setForm({...form, intervention_system: e.target.value})}
          className="w-full border h-8 text-xs bg-background" data-testid="wo-op-system">
          <option value="">—</option>{SYSTEMS.map(s => <option key={s} value={s}>{SYS_LABEL[s]}</option>)}
        </select>
      </Field>
      <Field label="Turno">
        <select value={form.shift} onChange={(e) => setForm({...form, shift: e.target.value})}
          className="w-full border h-8 text-xs bg-background" data-testid="wo-op-shift">
          <option value="">—</option>{SHIFTS.map(s => <option key={s} value={s}>{SHIFT_LABEL[s]}</option>)}
        </select>
      </Field>
      <Field label="Horómetro inicio">
        <Input type="number" step="0.1" value={form.hourmeter_start} onChange={(e) => setForm({...form, hourmeter_start: e.target.value})}
          className="rounded-none h-8 text-xs" data-testid="wo-op-hourmeter-start" />
      </Field>
      <Field label="Horómetro final">
        <Input type="number" step="0.1" value={form.hourmeter_end} onChange={(e) => setForm({...form, hourmeter_end: e.target.value})}
          className="rounded-none h-8 text-xs" data-testid="wo-op-hourmeter-end" />
      </Field>
      <Field label="N° técnicos">
        <Input type="number" min="1" value={form.technicians_count} onChange={(e) => setForm({...form, technicians_count: e.target.value})}
          className="rounded-none h-8 text-xs" data-testid="wo-op-tech-count" />
      </Field>
      <Field label="Supervisor">
        <Input value={form.supervisor} onChange={(e) => setForm({...form, supervisor: e.target.value})}
          className="rounded-none h-8 text-xs" data-testid="wo-op-supervisor" />
      </Field>
      <div className="col-span-2 sm:col-span-3 space-y-1">
        <Label className="text-[9px] font-bold uppercase tracking-widest">Herramientas planeadas (separadas por coma)</Label>
        <Input value={form.planned_tools} onChange={(e) => setForm({...form, planned_tools: e.target.value})}
          className="rounded-none h-8 text-xs" placeholder="Torquímetro, Multímetro, Extractor…" data-testid="wo-op-planned-tools" />
      </div>
      <div className="col-span-2 sm:col-span-3 space-y-1">
        <Label className="text-[9px] font-bold uppercase tracking-widest">Herramientas NO planeadas (separadas por coma)</Label>
        <Input value={form.unplanned_tools} onChange={(e) => setForm({...form, unplanned_tools: e.target.value})}
          className="rounded-none h-8 text-xs" placeholder="Gata hidráulica, Escalera, Grúa…" data-testid="wo-op-unplanned-tools" />
      </div>
      <div className="col-span-2 sm:col-span-3">
        <Button size="sm" onClick={commit} className="rounded-none text-xs font-bold" data-testid="wo-op-save">Guardar detalles operativos</Button>
      </div>
    </div>
  );
}

const statusStyle = (s) => ({
  created: "text-slate-700 bg-slate-100 border-slate-300",
  released: "text-blue-700 bg-blue-100 border-blue-300",
  in_progress: "text-amber-700 bg-amber-100 border-amber-300",
  tech_completed: "text-indigo-700 bg-indigo-100 border-indigo-300",
  closed: "text-emerald-700 bg-emerald-100 border-emerald-300",
  cancelled: "text-red-700 bg-red-100 border-red-300",
}[s] || "text-slate-600 bg-slate-100 border-slate-300");

export default function MntWorkOrderDetail() {
  const { t } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [wo, setWo] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [closingOpen, setClosingOpen] = useState(false);
  const [closingData, setClosingData] = useState({ root_cause: "", corrective_action: "" });
  const [techOpen, setTechOpen] = useState(false);
  const [techData, setTechData] = useState({ downtime_hours: 0 });
  const [addOpOpen, setAddOpOpen] = useState(false);
  const [newOp, setNewOp] = useState({ seq: 10, description: "", planned_hours: 0, actual_hours: 0, completed: false });
  const [addMatOpen, setAddMatOpen] = useState(false);
  const [newMat, setNewMat] = useState({ item_number: "", qty_planned: 1, unit_cost: 0, warehouse: "" });
  const [consumeOpen, setConsumeOpen] = useState(false);
  const [consume, setConsume] = useState({ item_number: "", qty: 0, warehouse: "", unit_cost: 0, cost_code: { subcuenta: "", tipo: "" } });
  const [ccHeader, setCcHeader] = useState({ client_code: "", contract_code: "", frente_code: "" });
  const [itemQuery, setItemQuery] = useState("");
  const [itemMatches, setItemMatches] = useState([]);
  const [availability, setAvailability] = useState(null);
  const [createReqOpen, setCreateReqOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);

  const editable = can("admin", "maintenance", "warehouse");

  const load = useCallback(() => {
    api.get(`/mnt/work-orders/${id}`).then((r) => {
      setWo(r.data);
      setCcHeader({ client_code: r.data.client_code || "", contract_code: r.data.contract_code || "", frente_code: r.data.frente_code || "" });
    }).catch(() => setWo(false));
    api.get(`/mnt/work-orders/${id}/material-availability`).then((r) => setAvailability(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/warehouses").then((r) => setWarehouses(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!itemQuery || itemQuery.length < 2) { setItemMatches([]); return; }
    const h = setTimeout(() => {
      api.get("/items/search", { params: { q: itemQuery, limit: 10 } }).then((r) => setItemMatches(r.data));
    }, 200);
    return () => clearTimeout(h);
  }, [itemQuery]);

  if (wo === null) return <div className="text-xs text-muted-foreground p-6">Cargando…</div>;
  if (wo === false) return <div className="text-xs text-muted-foreground p-6">Not found</div>;

  const setStatus = async (status, extra = {}) => {
    try {
      const { data } = await api.post(`/mnt/work-orders/${id}/status`, { status, ...extra });
      // Auto-consumption feedback (only fires on close)
      if (data && data._auto_consumption && data._auto_consumption.length) {
        const ok = data._auto_consumption.filter(x => x.reason === "ok").length;
        const short = data._auto_consumption.filter(x => x.reason !== "ok");
        toast.success(`Cierre OK · ${ok} material(es) descontado(s) del inventario`);
        short.forEach(s => toast.warning(
          `Faltante ${s.item_number}: consumido ${s.consumed}/${s.requested}`));
      } else {
        toast.success(t("mnt.wo.statusChanged"));
      }
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const updateOperation = async (idx, patch) => {
    const ops = wo.operations.map((o, i) => i === idx ? { ...o, ...patch } : o);
    await saveOpsAndMats(ops, wo.materials);
  };

  const removeOperation = async (idx) => {
    const ops = wo.operations.filter((_, i) => i !== idx);
    await saveOpsAndMats(ops, wo.materials);
  };

  const addOperation = async () => {
    const ops = [...wo.operations, newOp];
    await saveOpsAndMats(ops, wo.materials);
    setAddOpOpen(false); setNewOp({ seq: (wo.operations.length + 1) * 10, description: "", planned_hours: 0, actual_hours: 0, completed: false });
  };

  const addMaterial = async () => {
    const mats = [...wo.materials, { item_number: newMat.item_number, qty_planned: parseFloat(newMat.qty_planned) || 0, qty_consumed: 0, unit_cost: parseFloat(newMat.unit_cost) || 0, warehouse: newMat.warehouse || warehouses[0]?.code || "" }];
    await saveOpsAndMats(wo.operations, mats);
    setAddMatOpen(false); setNewMat({ item_number: "", qty_planned: 1, unit_cost: 0, warehouse: "" }); setItemQuery(""); setItemMatches([]);
  };

  const removeMaterial = async (idx) => {
    const mats = wo.materials.filter((_, i) => i !== idx);
    await saveOpsAndMats(wo.operations, mats);
  };

  const saveOpsAndMats = async (operations, materials, laborRateOverride, extra = {}) => {
    try {
      await api.put(`/mnt/work-orders/${id}`, {
        type: wo.type, equipment_id: wo.equipment_id, priority: wo.priority,
        description: wo.description, notification_id: wo.notification_id || "",
        assigned_to: wo.assigned_to || "",
        technician_name: wo.technician_name || "",
        planned_start: wo.planned_start || "", planned_end: wo.planned_end || "",
        labor_rate: laborRateOverride != null ? laborRateOverride : (wo.labor_rate || 0),
        operations, materials,
        equipment_status: wo.equipment_status || "", intervention_system: wo.intervention_system || "",
        hourmeter_start: wo.hourmeter_start || 0, hourmeter_end: wo.hourmeter_end || 0,
        planned_tools: wo.planned_tools || [], unplanned_tools: wo.unplanned_tools || [],
        shift: wo.shift || "", technicians_count: wo.technicians_count || 1,
        supervisor: wo.supervisor || "",
        ...extra,
      });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const doConsume = async () => {
    try {
      await api.post(`/mnt/work-orders/${id}/consume`, {
        item_number: consume.item_number,
        qty: parseFloat(consume.qty),
        warehouse: consume.warehouse,
        unit_cost: parseFloat(consume.unit_cost) || 0,
        cost_code: { subcuenta: consume.cost_code?.subcuenta || "", tipo: consume.cost_code?.tipo || "",
          client: ccHeader.client_code, contract: ccHeader.contract_code, frente: ccHeader.frente_code },
      });
      toast.success(t("mnt.wo.mat.consumed.ok"));
      setConsumeOpen(false); setConsume({ item_number: "", qty: 0, warehouse: "", unit_cost: 0, cost_code: { subcuenta: "", tipo: "" } });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openConsume = (m) => {
    setConsume({
      item_number: m.item_number, qty: (m.qty_planned || 0) - (m.qty_consumed || 0),
      warehouse: m.warehouse || (warehouses[0]?.code || ""), unit_cost: m.unit_cost || 0,
    });
    setConsumeOpen(true);
  };

  const createReqFromWO = async () => {
    try {
      const r = await api.post(`/mnt/work-orders/${id}/create-requisition`, {
        warehouse: warehouses[0]?.code || "",
      });
      toast.success(`Requisición creada: ${r.data.number}`);
      setCreateReqOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const canEdit = editable && !["closed", "cancelled"].includes(wo.status);

  return (
    <div data-testid="mnt-wo-detail-page">
      <button onClick={() => navigate("/mnt/work-orders")} className="text-xs text-muted-foreground flex items-center gap-1 mb-3 hover:text-foreground" data-testid="wo-back">
        <ArrowLeft size={12} /> {t("mnt.wo.title")}
      </button>

      <div className="border bg-card p-4 mb-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-black tabular-nums" data-testid="wo-number">{wo.wo_number}</h1>
              <span className={`inline-block border px-2 py-0.5 text-[10px] font-bold uppercase ${statusStyle(wo.status)}`} data-testid="wo-status">{t(`mnt.wo.status.${wo.status}`)}</span>
              <span className="text-[10px] font-bold uppercase bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5">{t(`mnt.wo.type.${wo.type}`)}</span>
              <span className="text-[10px] font-bold uppercase bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5">{t(`mnt.p.${wo.priority}`)}</span>
            </div>
            <div className="text-xs mt-2"><span className="font-bold">{wo.equipment_code}</span> · {wo.equipment_name}{wo.equipment_area && <span className="text-muted-foreground"> · {wo.equipment_area}</span>}</div>
            <div className="text-xs mt-1 text-muted-foreground">{wo.description}</div>
            {wo.notification_id && (
              <div className="text-[10px] text-emerald-700 mt-1">{t("mnt.wo.linkedNotif")}: <button onClick={() => navigate(`/mnt/notifications`)} className="underline">↗</button></div>
            )}
            {wo.requisition_number && (
              <div className="mt-2">
                <button onClick={() => navigate(`/requisitions?open=${wo.requisition_id}`)}
                  className="inline-flex items-center gap-1 text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 px-2 py-1 hover:bg-emerald-200"
                  data-testid="wo-linked-req">
                  <ShoppingCart size={11} /> Requisición: {wo.requisition_number} <ExternalLink size={10} />
                </button>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {wo.status === "in_progress" && wo.actual_start && (
              <LiveHHTimer actualStart={wo.actual_start} techs={wo.technicians_count || 1} />
            )}
            <button onClick={() => downloadReport(`/mnt/work-orders/${wo.id}/pdf`, `OT_${wo.wo_number}.pdf`).catch((e) => toast.error(errMsg(e)))}
              className="border px-2 h-8 text-[10px] font-bold hover:bg-muted flex items-center gap-1" data-testid="wo-print-pdf" title="Imprimir OT en PDF">
              <Printer size={12} /> PDF
            </button>
            {editable && WO_OPEN_STATUSES.includes(wo.status) && (
              <Button size="sm" variant="outline" onClick={() => setEditOpen(true)} className="rounded-none text-xs font-bold" data-testid="wo-edit"><Pencil size={12} className="mr-1" /> {t("mnt.wo.edit")}</Button>
            )}
            {editable && wo.status === "created" && (
              <Button size="sm" onClick={() => setStatus("released")} className="rounded-none text-xs font-bold" data-testid="wo-release"><Play size={12} className="mr-1" /> {t("mnt.wo.release")}</Button>
            )}
            {editable && wo.status === "released" && (
              <Button size="sm" onClick={() => setStatus("in_progress")} className="rounded-none text-xs font-bold" data-testid="wo-start"><Play size={12} className="mr-1" /> {t("mnt.wo.start")}</Button>
            )}
            {editable && ["created", "released", "in_progress"].includes(wo.status) && (
              <Button size="sm" variant="outline" onClick={() => setCancelOpen(true)} className="rounded-none text-xs font-bold text-red-600" data-testid="wo-cancel"><Ban size={12} className="mr-1" /> {t("mnt.wo.cancel")}</Button>
            )}
            {can("admin") && wo.status === "cancelled" && (
              <Button size="sm" onClick={() => setReopenOpen(true)} className="rounded-none text-xs font-bold" data-testid="wo-reopen"><RotateCcw size={12} className="mr-1" /> {t("mnt.wo.reopen")}</Button>
            )}
            <WOEditDialog wo={wo} open={editOpen} onOpenChange={setEditOpen} onSaved={load} />
            <WOCancelDialog wo={wo} open={cancelOpen} onOpenChange={setCancelOpen} onDone={load} />
            <WOReopenDialog wo={wo} open={reopenOpen} onOpenChange={setReopenOpen} onDone={load} />
            {editable && wo.status === "in_progress" && (
              <Dialog open={techOpen} onOpenChange={setTechOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="rounded-none text-xs font-bold" data-testid="wo-tech-complete"><Square size={12} className="mr-1" /> {t("mnt.wo.techComplete")}</Button>
                </DialogTrigger>
                <DialogContent className="rounded-none sm:max-w-md">
                  <DialogHeader><DialogTitle className="uppercase font-black text-sm">{t("mnt.wo.techComplete")}</DialogTitle></DialogHeader>
                  <div className="space-y-2">
                    <Label className="text-[10px] font-bold uppercase tracking-widest">{t("mnt.wo.downtime")}</Label>
                    <Input type="number" step="0.1" value={techData.downtime_hours}
                      onChange={(e) => setTechData({ downtime_hours: e.target.value })}
                      className="rounded-none h-8 text-xs" data-testid="wo-downtime-input" />
                  </div>
                  <DialogFooter>
                    <Button onClick={() => { setStatus("tech_completed", { downtime_hours: parseFloat(techData.downtime_hours) || 0 }); setTechOpen(false); }}
                      className="rounded-none text-xs font-bold" data-testid="wo-tech-complete-confirm">{t("common.save")}</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
            {editable && wo.status === "tech_completed" && <>
              <Dialog open={closingOpen} onOpenChange={setClosingOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="rounded-none text-xs font-bold" data-testid="wo-close"><CheckSquare size={12} className="mr-1" /> {t("mnt.wo.confirmClose")}</Button>
                </DialogTrigger>
                <DialogContent className="rounded-none sm:max-w-lg">
                  <DialogHeader><DialogTitle className="uppercase font-black text-sm">{t("mnt.wo.confirmClose")}</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label className="text-[10px] font-bold uppercase tracking-widest">{t("mnt.wo.rootCause")}</Label>
                      <Textarea rows={2} value={closingData.root_cause} onChange={(e) => setClosingData({ ...closingData, root_cause: e.target.value })}
                        className="rounded-none text-xs" data-testid="wo-root-cause" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] font-bold uppercase tracking-widest">{t("mnt.wo.action")}</Label>
                      <Textarea rows={2} value={closingData.corrective_action} onChange={(e) => setClosingData({ ...closingData, corrective_action: e.target.value })}
                        className="rounded-none text-xs" data-testid="wo-action" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={() => { setStatus("closed", closingData); setClosingOpen(false); }}
                      disabled={!closingData.root_cause || !closingData.corrective_action}
                      className="rounded-none text-xs font-bold" data-testid="wo-close-confirm">{t("common.save")}</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <Button size="sm" variant="outline" onClick={() => setStatus("in_progress")} className="rounded-none text-xs font-bold" data-testid="wo-reopen">{t("mnt.wo.reopen")}</Button>
            </>}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-4 border-t">
          <div><div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">{t("mnt.wo.assigned")}</div><div className="text-xs mt-1">{wo.assigned_to || "—"}</div></div>
          <div><div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">{t("mnt.wo.technician")}</div><div className="text-xs mt-1 font-bold">{wo.technician_name || "—"}</div></div>
          <div><div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">Supervisor</div><div className="text-xs mt-1">{wo.supervisor || "—"}</div></div>
          <div><div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">Turno</div><div className="text-xs mt-1 font-bold uppercase">{{"morning":"Matutino","afternoon":"Vespertino","night":"Nocturno"}[wo.shift] || "—"}</div></div>
          <div><div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">Estado del equipo</div><div className="text-xs mt-1 font-bold">{{"operational":"Operativo","out_of_service":"Fuera de servicio","reclamation":"Aprovechamiento","standby":"Stand-by","scheduled_maintenance":"Mant. programado"}[wo.equipment_status] || "—"}</div></div>
          <div><div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">Sistema intervenido</div><div className="text-xs mt-1">{{"electrical":"Eléctrico","engine":"Motor","transmission":"Transmisión","hydraulic":"Hidráulico","pneumatic":"Neumático","brakes":"Frenos","structural":"Estructural","cooling":"Refrigeración","fuel":"Combustible","steering":"Dirección","cabin":"Cabina","other":"Otro"}[wo.intervention_system] || "—"}</div></div>
          <div><div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">Horómetro inicio</div><div className="text-xs mt-1 tabular-nums">{wo.hourmeter_start || "—"}</div></div>
          <div><div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">Horómetro final</div><div className="text-xs mt-1 tabular-nums">{wo.hourmeter_end || "—"}</div></div>
          <div><div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">N° técnicos</div><div className="text-xs mt-1 tabular-nums font-bold">{wo.technicians_count || 1}</div></div>
          <div><div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">H.H total</div><div className="text-xs mt-1 tabular-nums font-bold text-primary">{fmtQty(wo.hh_labor_hours || 0)} h</div></div>
          <div><div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">{t("mnt.wo.plannedStart")}</div><div className="text-xs mt-1 tabular-nums">{wo.planned_start ? fmtDate(wo.planned_start) : "—"}</div></div>
          <div>
            <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">{t("mnt.wo.actualStart")}</div>
            {canEdit ? (
              <Input type="datetime-local"
                     defaultValue={(wo.actual_start || "").slice(0, 16)}
                     onBlur={(e) => saveOpsAndMats(wo.operations, wo.materials, wo.labor_rate, { actual_start: e.target.value })}
                     className="rounded-none h-7 text-xs tabular-nums mt-0.5"
                     data-testid="wo-actual-start-input" />
            ) : (
              <div className="text-xs mt-1 tabular-nums">{wo.actual_start ? fmtDate(wo.actual_start) : "—"}</div>
            )}
          </div>
          <div>
            <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">{t("mnt.wo.actualEnd")}</div>
            {canEdit ? (
              <Input type="datetime-local"
                     defaultValue={(wo.actual_end || "").slice(0, 16)}
                     onBlur={(e) => saveOpsAndMats(wo.operations, wo.materials, wo.labor_rate, { actual_end: e.target.value })}
                     className="rounded-none h-7 text-xs tabular-nums mt-0.5"
                     data-testid="wo-actual-end-input" />
            ) : (
              <div className="text-xs mt-1 tabular-nums">{wo.actual_end ? fmtDate(wo.actual_end) : "—"}</div>
            )}
          </div>
          <div><div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">{t("mnt.wo.downtime")}</div><div className="text-xs mt-1 font-bold tabular-nums">{fmtQty(wo.downtime_hours)} h</div></div>
          <div>
            <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">{t("mnt.wo.laborRate")}</div>
            {canEdit ? (
              <Input type="number" step="0.01" defaultValue={wo.labor_rate || 0}
                onBlur={(e) => saveOpsAndMats(wo.operations, wo.materials, parseFloat(e.target.value) || 0)}
                className="rounded-none h-7 text-xs w-24 tabular-nums mt-0.5" data-testid="wo-labor-rate" />
            ) : <div className="text-xs mt-1 tabular-nums">{fmtMoney(wo.labor_rate || 0)}</div>}
          </div>
          <div><div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">{t("mnt.wo.laborCost")}</div><div className="text-xs mt-1 tabular-nums">{fmtMoney(wo.labor_cost || 0)}</div></div>
          <div><div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">{t("mnt.wo.materialCost")}</div><div className="text-xs mt-1 tabular-nums">{fmtMoney(wo.materials_cost || 0)}</div></div>
          <div><div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">{t("mnt.wo.totalCost")}</div><div className="text-sm mt-1 font-black tabular-nums text-primary">{fmtMoney(wo.total_cost || 0)}</div></div>
        </div>

        {(wo.planned_tools?.length > 0 || wo.unplanned_tools?.length > 0) && (
          <div className="mt-4 pt-4 border-t text-xs" data-testid="wo-tools-section">
            <div className="text-[10px] font-bold uppercase tracking-widest mb-2">Herramientas / Equipo auxiliar</div>
            {wo.planned_tools?.length > 0 && (
              <div className="mb-2">
                <span className="text-[10px] font-bold text-emerald-700 uppercase">Planeadas:</span>{" "}
                {wo.planned_tools.map((t, i) => <span key={i} className="inline-block border px-1.5 py-0.5 mr-1 text-[10px] bg-emerald-50">{t}</span>)}
              </div>
            )}
            {wo.unplanned_tools?.length > 0 && (
              <div>
                <span className="text-[10px] font-bold text-amber-700 uppercase">No planeadas:</span>{" "}
                {wo.unplanned_tools.map((t, i) => <span key={i} className="inline-block border px-1.5 py-0.5 mr-1 text-[10px] bg-amber-50">{t}</span>)}
              </div>
            )}
          </div>
        )}

        {canEdit && (
          <div className="mt-4 pt-4 border-t" data-testid="wo-operational-editor">
            <details open>
              <summary className="text-[10px] font-bold uppercase tracking-widest cursor-pointer text-primary hover:underline">
                Detalles operativos (turno, horómetro, herramientas, sistema intervenido…)
              </summary>
              <OperationalEditor wo={wo} onSave={async (patch) => {
                try {
                  await api.put(`/mnt/work-orders/${wo.id}`, {
                    type: wo.type, equipment_id: wo.equipment_id, priority: wo.priority,
                    description: wo.description, notification_id: wo.notification_id || "",
                    assigned_to: wo.assigned_to || "", technician_name: wo.technician_name || "",
                    planned_start: wo.planned_start || "", planned_end: wo.planned_end || "",
                    labor_rate: wo.labor_rate || 0,
                    operations: wo.operations || [], materials: wo.materials || [],
                    equipment_status: wo.equipment_status || "", intervention_system: wo.intervention_system || "",
                    hourmeter_start: wo.hourmeter_start || 0, hourmeter_end: wo.hourmeter_end || 0,
                    planned_tools: wo.planned_tools || [], unplanned_tools: wo.unplanned_tools || [],
                    shift: wo.shift || "", technicians_count: wo.technicians_count || 1,
                    supervisor: wo.supervisor || "",
                    ...patch,
                  });
                  toast.success("Actualizado");
                  load();
                } catch (e) { toast.error(errMsg(e)); }
              }} />
            </details>
          </div>
        )}

        <WOAuditHistory wid={wo.id} />

        {wo.status === "cancelled" && (
          <div className="mt-4 pt-4 border-t text-xs" data-testid="wo-cancel-info">
            <div className="font-bold uppercase tracking-widest text-[9px] text-red-700 mb-1">{t("mnt.wo.cancelReason")}</div>
            <div>{wo.cancel_reason || "—"}</div>
            {wo.cancelled_by && <div className="text-[10px] text-muted-foreground mt-1">{t("mnt.wo.cancelledBy")}: {wo.cancelled_by} · {fmtDate(wo.cancelled_at)}</div>}
          </div>
        )}
        {wo.reopen_reason && (
          <div className="mt-4 pt-4 border-t text-xs" data-testid="wo-reopen-info">
            <div className="font-bold uppercase tracking-widest text-[9px] text-amber-700 mb-1">{t("mnt.wo.reopenReason")}</div>
            <div>{wo.reopen_reason}</div>
            <div className="text-[10px] text-muted-foreground mt-1">{t("mnt.wo.reopenedBy")}: {wo.reopened_by} · {fmtDate(wo.reopened_at)}</div>
          </div>
        )}
        {wo.root_cause && (
          <div className="mt-4 pt-4 border-t text-xs">
            <div className="font-bold uppercase tracking-widest text-[9px] text-muted-foreground mb-1">{t("mnt.wo.rootCause")}</div>
            <div>{wo.root_cause}</div>
            <div className="font-bold uppercase tracking-widest text-[9px] text-muted-foreground mt-2 mb-1">{t("mnt.wo.action")}</div>
            <div>{wo.corrective_action}</div>
          </div>
        )}
      </div>

      {/* Operations */}
      <div className="border bg-card mb-4">
        <div className="border-b p-3 flex items-center justify-between">
          <div className="text-[10px] font-bold uppercase tracking-widest">{t("mnt.wo.operations")}</div>
          {canEdit && (
            <Dialog open={addOpOpen} onOpenChange={setAddOpOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="rounded-none text-[10px] h-7 font-bold" data-testid="wo-add-op"><Plus size={11} className="mr-1" /> {t("mnt.wo.op.add")}</Button>
              </DialogTrigger>
              <DialogContent className="rounded-none sm:max-w-md">
                <DialogHeader><DialogTitle className="uppercase font-black text-sm">{t("mnt.wo.op.add")}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label className="text-[10px] font-bold uppercase">{t("mnt.wo.op.seq")}</Label><Input type="number" value={newOp.seq} onChange={(e) => setNewOp({ ...newOp, seq: parseInt(e.target.value) || 0 })} className="rounded-none h-8 text-xs" data-testid="op-seq" /></div>
                  <div className="space-y-1"><Label className="text-[10px] font-bold uppercase">{t("mnt.wo.op.plannedH")}</Label><Input type="number" step="0.1" value={newOp.planned_hours} onChange={(e) => setNewOp({ ...newOp, planned_hours: parseFloat(e.target.value) || 0 })} className="rounded-none h-8 text-xs" data-testid="op-planned" /></div>
                  <div className="col-span-2 space-y-1"><Label className="text-[10px] font-bold uppercase">{t("mnt.description")}</Label><Input value={newOp.description} onChange={(e) => setNewOp({ ...newOp, description: e.target.value })} className="rounded-none h-8 text-xs" data-testid="op-desc" /></div>
                </div>
                <DialogFooter><Button onClick={addOperation} disabled={!newOp.description} className="rounded-none text-xs font-bold" data-testid="op-save">{t("common.save")}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
        {(!wo.operations || wo.operations.length === 0) ? <div className="text-center text-[11px] text-muted-foreground py-6">—</div> : (
          <table className="tbl">
            <thead><tr><th>{t("mnt.wo.op.seq")}</th><th>{t("mnt.description")}</th><th>{t("mnt.wo.op.plannedH")}</th><th>{t("mnt.wo.op.actualH")}</th><th>{t("mnt.wo.op.done")}</th><th></th></tr></thead>
            <tbody>
              {wo.operations.map((op, i) => (
                <tr key={i} data-testid={`wo-op-${i}`}>
                  <td className="tabular-nums font-bold">{op.seq}</td>
                  <td className="text-[11px]">{op.description}</td>
                  <td className="tabular-nums">{fmtQty(op.planned_hours)}</td>
                  <td>{canEdit ? <Input type="number" step="0.1" defaultValue={op.actual_hours} onBlur={(e) => updateOperation(i, { actual_hours: parseFloat(e.target.value) || 0 })} className="rounded-none h-7 text-xs w-20 tabular-nums" data-testid={`op-actual-${i}`} /> : fmtQty(op.actual_hours)}</td>
                  <td>{canEdit ? <input type="checkbox" checked={!!op.completed} onChange={(e) => updateOperation(i, { completed: e.target.checked })} data-testid={`op-done-${i}`} /> : (op.completed ? "✓" : "—")}</td>
                  <td>{canEdit && <button onClick={() => removeOperation(i)} className="text-muted-foreground hover:text-red-600" data-testid={`op-del-${i}`}><Trash2 size={11} /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Material Availability & Purchasing Bridge */}
      {availability && availability.lines.length > 0 && (
        <div className="border bg-card mb-4" data-testid="wo-availability-panel">
          <div className="border-b p-3 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              {availability.total_shortage_value > 0 && <AlertTriangle size={14} className="text-amber-600" />}
              <div className="text-[10px] font-bold uppercase tracking-widest">Disponibilidad de material</div>
              {availability.total_shortage_value > 0 && (
                <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5">
                  Faltante: {fmtMoney(availability.total_shortage_value)}
                </span>
              )}
            </div>
            {canEdit && availability.total_shortage_value > 0 && !wo.requisition_number && (
              <Button size="sm" onClick={() => setCreateReqOpen(true)}
                className="rounded-none text-[10px] h-7 font-bold" data-testid="wo-create-req">
                <ShoppingCart size={11} className="mr-1" /> Crear Requisición
              </Button>
            )}
          </div>
          <table className="tbl">
            <thead><tr>
              <th>Artículo</th><th>Planeado</th><th>Consumido</th><th>Necesita</th>
              <th>En existencia</th><th>Reservado</th><th>Libre</th><th>Faltante</th>
            </tr></thead>
            <tbody>
              {availability.lines.map((l, i) => (
                <tr key={i} data-testid={`avail-row-${l.item_number}`}
                    className={l.shortage > 0 ? "bg-amber-50 dark:bg-amber-950/20" : ""}>
                  <td className="text-[11px]"><span className="font-bold tabular-nums">{l.item_number}</span> · {l.item_description}</td>
                  <td className="tabular-nums">{fmtQty(l.planned)} {l.uom}</td>
                  <td className="tabular-nums text-muted-foreground">{fmtQty(l.consumed)}</td>
                  <td className="tabular-nums font-bold">{fmtQty(l.need)}</td>
                  <td className="tabular-nums">{fmtQty(l.on_hand)}</td>
                  <td className="tabular-nums text-blue-700">{fmtQty(l.reserved)}</td>
                  <td className="tabular-nums font-bold">{fmtQty(l.free)}</td>
                  <td className={`tabular-nums font-bold ${l.shortage > 0 ? "text-red-700" : "text-emerald-700"}`}>
                    {l.shortage > 0 ? fmtQty(l.shortage) : "✓"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Dialog: Crear Requisición desde OT */}
      <Dialog open={createReqOpen} onOpenChange={setCreateReqOpen}>
        <DialogContent className="rounded-none sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="uppercase font-black text-sm">Crear Requisición desde esta OT</DialogTitle>
            <DialogDescription className="text-xs">
              Se creará una requisición <b>borrador</b> categoría <b>maintenance</b> con las líneas en escasez,
              taggeadas a este equipo y esta OT. El comprador/aprobador la revisará antes de enviar a aprobación.
            </DialogDescription>
          </DialogHeader>
          <div className="border p-3 bg-muted/30 text-[11px]">
            <div className="font-bold mb-1">Líneas a incluir:</div>
            {availability?.lines.filter(l => l.shortage > 0).map((l, i) => (
              <div key={i} className="flex justify-between py-0.5">
                <span><span className="font-bold tabular-nums">{l.item_number}</span> · {l.item_description}</span>
                <span className="tabular-nums font-bold text-amber-700">{fmtQty(l.shortage)} {l.uom}</span>
              </div>
            ))}
            <div className="mt-2 pt-2 border-t font-bold flex justify-between">
              <span>Total estimado:</span>
              <span className="tabular-nums text-amber-700">{fmtMoney(availability?.total_shortage_value || 0)}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateReqOpen(false)} className="rounded-none text-xs" data-testid="wo-create-req-cancel">Cancelar</Button>
            <Button onClick={createReqFromWO} className="rounded-none text-xs font-bold" data-testid="wo-create-req-confirm">
              Crear Requisición
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Materials */}
      <div className="border bg-card">
        <div className="border-b p-3 flex items-center justify-between">
          <div className="text-[10px] font-bold uppercase tracking-widest">{t("mnt.wo.materials")}</div>
          {canEdit && (
            <Dialog open={addMatOpen} onOpenChange={(o) => { setAddMatOpen(o); if (!o) { setItemQuery(""); setItemMatches([]); } }}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="rounded-none text-[10px] h-7 font-bold" data-testid="wo-add-mat"><Plus size={11} className="mr-1" /> {t("mnt.wo.mat.add")}</Button>
              </DialogTrigger>
              <DialogContent className="rounded-none sm:max-w-md">
                <DialogHeader><DialogTitle className="uppercase font-black text-sm">{t("mnt.wo.mat.add")}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div className="space-y-1 relative">
                    <Label className="text-[10px] font-bold uppercase">{t("nav.items")}</Label>
                    <Input value={itemQuery} onChange={(e) => setItemQuery(e.target.value)} placeholder="Código, descripción o No. de parte…" className="rounded-none h-8 text-xs" data-testid="mat-search" />
                    {itemMatches.length > 0 && (
                      <div className="absolute z-10 top-full left-0 right-0 mt-1 border bg-card max-h-56 overflow-auto shadow-lg">
                        {itemMatches.map((it) => (
                          <div key={it.id} onClick={() => { setNewMat({ ...newMat, item_number: it.item_number, unit_cost: it.standard_cost || 0 }); setItemQuery(`${it.item_number} · ${it.description}`); setItemMatches([]); }}
                            className="px-2 py-1.5 text-xs hover:bg-muted cursor-pointer" data-testid={`mat-match-${it.item_number}`}>
                            <span className="font-bold tabular-nums">{it.item_number}</span>
                            {it.part_number && <span className="ml-1 font-mono text-[10px] text-amber-700 dark:text-amber-400">NP {it.part_number}</span>} · {it.description}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1"><Label className="text-[10px] font-bold uppercase">{t("mnt.wo.mat.planned")}</Label><Input type="number" step="0.01" value={newMat.qty_planned} onChange={(e) => setNewMat({ ...newMat, qty_planned: e.target.value })} className="rounded-none h-8 text-xs" data-testid="mat-planned" /></div>
                    <div className="space-y-1"><Label className="text-[10px] font-bold uppercase">{t("mnt.wo.mat.unitCost")}</Label><Input type="number" step="0.01" value={newMat.unit_cost} onChange={(e) => setNewMat({ ...newMat, unit_cost: e.target.value })} className="rounded-none h-8 text-xs" data-testid="mat-cost" /></div>
                  </div>
                  <div className="space-y-1"><Label className="text-[10px] font-bold uppercase">{t("mnt.wo.mat.warehouse")}</Label>
                    <Select value={newMat.warehouse || warehouses[0]?.code || ""} onValueChange={(v) => setNewMat({ ...newMat, warehouse: v })}>
                      <SelectTrigger className="rounded-none h-8 text-xs" data-testid="mat-warehouse"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{warehouses.map((w) => <SelectItem key={w.code} value={w.code}>{w.code} · {w.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter><Button onClick={addMaterial} disabled={!newMat.item_number} className="rounded-none text-xs font-bold" data-testid="mat-save">{t("common.save")}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
        {(!wo.materials || wo.materials.length === 0) ? <div className="text-center text-[11px] text-muted-foreground py-6">—</div> : (
          <table className="tbl">
            <thead><tr><th>{t("nav.items")}</th><th>{t("mnt.wo.mat.planned")}</th><th>{t("mnt.wo.mat.consumed")}</th><th>{t("mnt.wo.mat.unitCost")}</th><th>{t("common.total") || "Total"}</th><th></th></tr></thead>
            <tbody>
              {wo.materials.map((m, i) => (
                <tr key={i} data-testid={`wo-mat-${i}`}>
                  <td className="text-[11px]"><span className="font-bold tabular-nums">{m.item_number}</span> · {m.item_description}</td>
                  <td className="tabular-nums">{fmtQty(m.qty_planned)}</td>
                  <td className="tabular-nums font-bold">{fmtQty(m.qty_consumed)}</td>
                  <td className="tabular-nums">{fmtMoney(m.unit_cost)}</td>
                  <td className="tabular-nums">{fmtMoney((m.qty_consumed || 0) * (m.unit_cost || 0))}</td>
                  <td className="flex gap-1">
                    {canEdit && wo.status === "in_progress" && (
                      <button onClick={() => openConsume(m)} className="text-emerald-600 hover:opacity-70" title={t("mnt.wo.mat.consume")} data-testid={`mat-consume-${i}`}>
                        <PackageMinus size={12} />
                      </button>
                    )}
                    {canEdit && (m.qty_consumed || 0) === 0 && (
                      <button onClick={() => removeMaterial(i)} className="text-muted-foreground hover:text-red-600" data-testid={`mat-del-${i}`}><Trash2 size={11} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Consume Modal */}
      <Dialog open={consumeOpen} onOpenChange={setConsumeOpen}>
        <DialogContent className="rounded-none sm:max-w-md">
          <DialogHeader><DialogTitle className="uppercase font-black text-sm">{t("mnt.wo.mat.consume")}</DialogTitle>
            <DialogDescription className="text-xs tabular-nums">{consume.item_number}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-[10px] font-bold uppercase">Qty</Label><Input type="number" step="0.01" value={consume.qty} onChange={(e) => setConsume({ ...consume, qty: e.target.value })} className="rounded-none h-8 text-xs" data-testid="consume-qty" /></div>
            <div className="space-y-1"><Label className="text-[10px] font-bold uppercase">{t("mnt.wo.mat.warehouse")}</Label>
              <Select value={consume.warehouse} onValueChange={(v) => setConsume({ ...consume, warehouse: v })}>
                <SelectTrigger className="rounded-none h-8 text-xs" data-testid="consume-warehouse"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{warehouses.map((w) => <SelectItem key={w.code} value={w.code}>{w.code} · {w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1"><Label className="text-[10px] font-bold uppercase">{t("mnt.wo.mat.unitCost")}</Label><Input type="number" step="0.01" value={consume.unit_cost} onChange={(e) => setConsume({ ...consume, unit_cost: e.target.value })} className="rounded-none h-8 text-xs" data-testid="consume-cost" /></div>
            <div className="col-span-2 space-y-1">
              <CostCodeHeader value={ccHeader} onChange={(patch) => setCcHeader({ ...ccHeader, ...patch })} testid="consume-cc" />
              <AccountPicker value={consume.cost_code} onChange={(patch) => setConsume({ ...consume, cost_code: { ...(consume.cost_code || {}), ...patch } })}
                header={ccHeader} equipmentCode={wo?.equipment_code} deptoCode="2100" testid="consume-line-cc" />
              <p className="text-[9px] text-muted-foreground">Si no eliges subcuenta se usa 6401 (correctivo) o 6402 (preventivo) con el equipo de la OT.</p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={doConsume} disabled={!consume.item_number || !consume.warehouse || parseFloat(consume.qty) <= 0}
              className="rounded-none text-xs font-bold" data-testid="consume-confirm">{t("mnt.wo.mat.consume")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
